import styled from '@emotion/styled';
import { Icon } from '@mdi/react';
import { AttachmentButton, IAttachmentButtonProps } from '..';
import { BaseArea, BaseButton, BaseCheckbox, BaseInput, Toggle, Variables } from '../../base';

interface INote {
    isNote: boolean;
}

interface ILockable {
    locked: boolean;
}

export const Controls = styled.div<INote>`
    padding: 8px;
    ${(props: INote) => props.isNote && noteControls}
    position: relative;
    border-color: ${Variables.ThemeLight.Border3};
    &::before {
        content: '';
        position: absolute;
        top: -72px;
        left: 0;
        right: 0;
        height: 72px;
        pointer-events: none;
        z-index: 10;
        background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, ${Variables.Colors.ghostWhite} 100%);
    }
`;

export const InputWrapper = styled.div`
    display: flex;
    align-items: flex-start;
`;

export const InputContainer = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    margin: 0px 16px;
    padding: 16px 16px 8px 16px;
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.slate300};
    border-radius: ${Variables.Rounded.xlarge};
    box-shadow: ${Variables.Shadows.small};
`;

export const InputBox = styled.div<ILockable>`
    display: flex;
    flex-grow: 1;
    align-items: flex-end;
    min-height: 40px;
    background-color: transparent;
    border: 0;
    border-radius: 0;
    border-color: ${Variables.ThemeLight.Border3};
    ${(props: ILockable) =>
        props.locked &&
        `
        opacity: 0.7;
    `}

    & textarea {
        font-size: ${Variables.FontSizes.tiny};
        background: transparent;
    }
`;
export const InputAttachments = styled.div`
    padding-bottom: 16px;
    margin-bottom: 6px;
    border-bottom: 1px solid ${Variables.Colors.platinum};
`;

export const HiddenInputWrapper = styled.div`
    display: none;
`;

// Explicit any-type to avoid leaking non-portable React type references
export const AttachInput: any = styled(BaseInput)`
    display: none;
`;
export const AttachButton = styled(BaseButton)`
    padding: 0 10px;
    min-width: auto;
    min-height: 36px;
    background-color: transparent;
    border: none;
    border-radius: ${Variables.Rounded.large};
    color: ${Variables.Colors.n100};

    &:hover:not(:disabled) {
        background-color: ${Variables.Colors.n15_16};
    }
`;
export const AttachIcon = styled(Icon)`
    transform: rotate(45deg);
    vertical-align: middle;
    fill: ${Variables.Colors.n100};
`;

export const StyledAttachmentButton = styled(AttachmentButton)<IAttachmentButtonProps>`
    min-width: 250px;
    max-width: 275px;
`;

export const Textarea = styled(BaseArea)`
    flex-grow: 1;
    font-size: 14px;
    line-height: 20px;
    background-color: transparent;
    border: 0;
    min-height: 24px;
    outline: none;
    resize: none;

    &::placeholder {
        color: ${Variables.Colors.slate400};
    }
`;
export const SendButton = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 50%;
`;

export const SendButtonWithLabel = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    width: auto;
    height: 40px;
    padding: 0 16px 0 12px;
    border-radius: 4px;
    gap: 6px;
`;

export const SendButtonLabel = styled.span`
    display: none;
`;

export const SendButtonLabelVisible = styled.span`
    display: inline-block;
`;

export const SendIcon = styled(Icon)`
    vertical-align: middle;
    flex-shrink: 0;
`;

export const Col = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    align-self: center;
`;
export const NoteCheckbox = styled(BaseCheckbox)`
    input[type='checkbox']:checked + div {
        border: 1px solid ${Variables.ThemeLight.PrimaryConstant6};
        background-color: ${Variables.ThemeLight.PrimaryConstant6};
        color: ${Variables.ThemeLight.PrimaryConstant11};
    }
    & span:last-child {
        color: ${Variables.ThemeLight.TextMedium};
        font-weight: ${Variables.FontWeight.medium};
        font-size: ${Variables.FontSizes.tiny};
    }
`;

export const NoteToggle = styled(Toggle)`
    --toggle-bgcolor-on: ${Variables.Colors.b200};
`;

export const NoteToggleWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const NoteLabel = styled.span`
    font-weight: ${Variables.FontWeight.medium};
    font-size: ${Variables.FontSizes.tiny};
    leading-trim: NONE;
    line-height: 100%;
    letter-spacing: 0%;
    color: ${Variables.ThemeLight.TextMedium};
`;

const noteControls = `
    ${InputContainer} {
        background: linear-gradient(180deg, ${Variables.ThemeLight.NeutralMild3} 0%, ${Variables.ThemeLight.Background3} 100%);
        border-color: ${Variables.Colors.b300_l90};
        box-shadow: 0px 6px 12px 0px ${Variables.ThemeLight.OverlayDark2};
    }
    ${InputBox} textarea {
        color: ${Variables.Colors.b300_l62};
    }
`;

export const InputError = styled.div`
    color: ${Variables.Colors.warning};
    font-size: 14px;
    fill: ${Variables.Colors.warning};
    margin-top: 5px;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
`;

export const InputErrorIcon = styled(Icon)`
    width: 18px;
    height: 18px;
    vertical-align: middle;
`;

export const InputErrorMessage = styled.div`
    display: inline-block;
    margin-left: 4px;
`;

export const ActionsWrapper = styled.div`
    margin-top: 8px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

export const ActionsItem = styled.div`
    margin-right: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const ActionsCol = styled.div`
    display: flex;
    gap: 4px:
    ${ActionsItem}:first-of-type {
        margin-left: 0;
    }
`;

export const DragOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${Variables.Colors.nx16};
    color: ${Variables.Colors.b300};
    font-size: 16px;
    pointer-events: none;
`;
export const QuickAnswersContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
`;

export const QuickAnswerBubble = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding: 0 12px;
    background-color: ${Variables.ThemeLight.NeutralOpacity2};
    opacity: 1;
    color: ${Variables.ThemeLight.TextMedium};
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: ${Variables.ThemeLight.NeutralOpacity3};
    }

    &:active {
        background-color: ${Variables.ThemeLight.NeutralOpacity5};
    }
`;
